/**
 * Graph Validation Utilities
 *
 * Centralized validation functions for AgentsFlow graph.
 * All functions are pure and don't cause side effects.
 */

// =============================================================================
// LLM CONFIG VALIDATION
// =============================================================================

const VALID_PROVIDERS = ['openai', 'anthropic', 'google'];

/**
 * Validates LLM configuration
 * @param {Object} llmConfig - The LLM configuration object
 * @returns {Object} { isValid: boolean, errors: string[], status: 'complete'|'incomplete'|'empty' }
 */
export const validateLLMConfig = (llmConfig) => {
  const errors = [];

  if (!llmConfig || Object.keys(llmConfig).length === 0) {
    return {
      isValid: false,
      errors: ['Configuração de LLM não definida'],
      status: 'empty',
    };
  }

  if (!llmConfig.provider) {
    errors.push('Selecione um provedor de IA (OpenAI, Anthropic, etc.)');
  } else if (!VALID_PROVIDERS.includes(llmConfig.provider)) {
    errors.push(`Provedor inválido: ${llmConfig.provider}`);
  }

  if (!llmConfig.model) {
    errors.push('Selecione um modelo de IA');
  }

  return {
    isValid: errors.length === 0,
    errors,
    status: errors.length === 0 ? 'complete' : 'incomplete',
  };
};

// =============================================================================
// BIFURCATION VALIDATION
// =============================================================================

/**
 * Validates that non-condition nodes don't have multiple outgoing edges
 * @param {Array} nodes - Array of nodes
 * @param {Array} edges - Array of edges
 * @returns {Object} { isValid: boolean, errors: Array<{nodeId, nodeName, message}> }
 */
export const validateNoBifurcation = (nodes, edges) => {
  const errors = [];

  // Group edges by source node
  const edgesBySource = new Map();
  edges.forEach(edge => {
    const existing = edgesBySource.get(edge.source) || [];
    existing.push(edge);
    edgesBySource.set(edge.source, existing);
  });

  // Check each node
  nodes.forEach(node => {
    // Skip condition nodes - they CAN have multiple outputs
    if (node.type === 'condition') return;

    // Skip end nodes - they don't have outputs
    if (node.type === 'end') return;

    const nodeEdges = edgesBySource.get(node.id) || [];

    if (nodeEdges.length > 1) {
      errors.push({
        nodeId: node.id,
        nodeName: node.data?.label || node.id,
        edgeCount: nodeEdges.length,
        message: `O nó "${node.data?.label || node.id}" tem ${nodeEdges.length} conexões de saída. Apenas nós de Condição podem ter múltiplas saídas.`,
      });
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Checks if a new connection would create an invalid bifurcation
 * @param {Object} sourceNode - The source node
 * @param {Array} existingEdges - Current edges in the graph
 * @param {string} sourceId - Source node ID
 * @returns {boolean} true if connection would be invalid
 */
export const wouldCreateBifurcation = (sourceNode, existingEdges, sourceId) => {
  // Condition nodes can have multiple outputs
  if (sourceNode?.type === 'condition') return false;

  // Check if source already has an outgoing edge
  const existingOutgoing = existingEdges.filter(e => e.source === sourceId);
  return existingOutgoing.length > 0;
};

// =============================================================================
// CONDITION NODE VALIDATION
// =============================================================================

/**
 * Validates that all condition nodes have both true and false connections
 * @param {Array} nodes - Array of nodes
 * @param {Array} edges - Array of edges
 * @returns {Object} { isValid: boolean, errors: Array<{nodeId, nodeName, missing, message}> }
 */
export const validateConditionEdges = (nodes, edges) => {
  const errors = [];

  const conditionNodes = nodes.filter(n => n.type === 'condition');

  conditionNodes.forEach(node => {
    const isRouter = node.data?.key === 'intent_router';
    const nodeEdges = edges.filter(e => e.source === node.id);

    if (isRouter) {
      // Router: precisa de pelo menos 1 edge nomeada + edge 'default'
      const hasDefault = nodeEdges.some(e => e.sourceHandle === 'default');
      const hasNamedRoute = nodeEdges.some(e => e.sourceHandle && e.sourceHandle !== 'default');

      if (!hasDefault || !hasNamedRoute) {
        const missing = [];
        if (!hasDefault) missing.push('Fallback (default)');
        if (!hasNamedRoute) missing.push('Rota nomeada');

        errors.push({
          nodeId: node.id,
          nodeName: node.data?.label || 'Roteador',
          missing,
          message: `O roteador "${node.data?.label || 'Roteador'}" precisa de pelo menos uma rota e um fallback.`,
        });
      }
    } else {
      // Condition binária: precisa true + false
      const hasTrue = nodeEdges.some(e => e.sourceHandle === 'true');
      const hasFalse = nodeEdges.some(e => e.sourceHandle === 'false');

      if (!hasTrue || !hasFalse) {
        const missing = [];
        if (!hasTrue) missing.push('Sim');
        if (!hasFalse) missing.push('Não');

        errors.push({
          nodeId: node.id,
          nodeName: node.data?.label || 'Condição',
          missing,
          hasTrue,
          hasFalse,
          message: `O nó de condição "${node.data?.label || 'Condição'}" precisa de conexões para: ${missing.join(' e ')}`,
        });
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Gets the connection status for a specific condition node
 * @param {string} nodeId - The condition node ID
 * @param {Array} edges - Array of edges
 * @returns {Object} { hasTrue: boolean, hasFalse: boolean, isComplete: boolean }
 */
export const getConditionNodeStatus = (nodeId, edges) => {
  const nodeEdges = edges.filter(e => e.source === nodeId);
  const hasTrue = nodeEdges.some(e => e.sourceHandle === 'true');
  const hasFalse = nodeEdges.some(e => e.sourceHandle === 'false');

  return {
    hasTrue,
    hasFalse,
    isComplete: hasTrue && hasFalse,
  };
};

// =============================================================================
// GRAPH CONNECTIVITY VALIDATION
// =============================================================================

/**
 * Validates that all nodes are connected (no orphan nodes)
 * @param {Array} nodes - Array of nodes
 * @param {Array} edges - Array of edges
 * @returns {Object} { isValid: boolean, orphanNodes: Array }
 */
export const validateConnectivity = (nodes, edges) => {
  const connectedNodeIds = new Set();

  // Add all nodes that are source or target of an edge
  edges.forEach(edge => {
    connectedNodeIds.add(edge.source);
    connectedNodeIds.add(edge.target);
  });

  // Start node is always considered connected
  const startNode = nodes.find(n => n.type === 'start');
  if (startNode) {
    connectedNodeIds.add(startNode.id);
  }

  // Find orphan nodes (excluding start which is always valid)
  const orphanNodes = nodes.filter(node =>
    node.type !== 'start' && !connectedNodeIds.has(node.id)
  );

  return {
    isValid: orphanNodes.length === 0,
    orphanNodes: orphanNodes.map(n => ({
      nodeId: n.id,
      nodeName: n.data?.label || n.id,
      nodeType: n.type,
    })),
  };
};

// =============================================================================
// SINGLE INSTANCE VALIDATION (e.g. data_collection max 1)
// =============================================================================

/** Nodes/capabilities that can only appear once in the graph */
const SINGLE_INSTANCE_KEYS = ['data_collection', 'end'];

/** Labels amigáveis para mensagens de erro */
const SINGLE_INSTANCE_LABELS = {
  data_collection: 'Coleta de Dados',
  end: 'Fim',
};

/**
 * Validates that single-instance capabilities are not duplicated
 * @param {Array} nodes - Array of nodes
 * @returns {Object} { isValid: boolean, errors: Array<{nodeId, message}> }
 */
export const validateSingleInstance = (nodes) => {
  const errors = [];
  const counts = {};

  nodes.forEach(node => {
    const key = node.data?.key;
    if (key && SINGLE_INSTANCE_KEYS.includes(key)) {
      counts[key] = (counts[key] || 0) + 1;
    }
  });

  Object.entries(counts).forEach(([key, count]) => {
    if (count > 1) {
      const label = SINGLE_INSTANCE_LABELS[key] || key;
      errors.push({
        type: 'single_instance',
        nodeKey: key,
        count,
        message: `Apenas 1 nó de "${label}" é permitido por fluxo (encontrados: ${count}). Remova os nós duplicados.`,
      });
    }
  });

  return { isValid: errors.length === 0, errors };
};

// =============================================================================
// COMPLETE GRAPH VALIDATION
// =============================================================================

/**
 * Performs complete graph validation
 * @param {Array} nodes - Array of nodes
 * @param {Array} edges - Array of edges
 * @param {Object} agentConfig - Agent configuration (includes llm_config)
 * @returns {Object} Complete validation result
 */
export const validateGraph = (nodes, edges, agentConfig = {}) => {
  const results = {
    llm: validateLLMConfig(agentConfig.llm_config),
    bifurcation: validateNoBifurcation(nodes, edges),
    conditionEdges: validateConditionEdges(nodes, edges),
    connectivity: validateConnectivity(nodes, edges),
    singleInstance: validateSingleInstance(nodes),
  };

  const allErrors = [];

  // Collect all errors with their types
  if (!results.llm.isValid) {
    results.llm.errors.forEach(e => allErrors.push({ type: 'llm', message: e }));
  }

  if (!results.bifurcation.isValid) {
    results.bifurcation.errors.forEach(e => allErrors.push({ type: 'bifurcation', ...e }));
  }

  if (!results.conditionEdges.isValid) {
    results.conditionEdges.errors.forEach(e => allErrors.push({ type: 'condition', ...e }));
  }

  if (!results.connectivity.isValid) {
    results.connectivity.orphanNodes.forEach(n => allErrors.push({
      type: 'connectivity',
      nodeId: n.nodeId,
      message: `O nó "${n.nodeName}" não está conectado ao fluxo`
    }));
  }

  if (!results.singleInstance.isValid) {
    results.singleInstance.errors.forEach(e => allErrors.push({ type: 'single_instance', ...e }));
  }

  // Determine what actions are allowed.
  // canSave: tolerante a nós de condição incompletos (usuário pode salvar
  //   trabalho em progresso). Só bloqueia em problemas estruturais que podem
  //   corromper o grafo no backend (bifurcação inválida, duplicação de nós
  //   single-instance).
  // canPublish: exige tudo — incluindo todas as ramificações de condição
  //   completas e LLM configurada.
  const canSave = results.bifurcation.isValid && results.singleInstance.isValid;
  const canPublish = canSave && results.conditionEdges.isValid && results.llm.isValid;

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
    results,
    canSave,
    canPublish,
    summary: {
      llmStatus: results.llm.status,
      hasOrphanNodes: !results.connectivity.isValid,
      hasBifurcationErrors: !results.bifurcation.isValid,
      hasIncompleteConditions: !results.conditionEdges.isValid,
      hasDuplicateNodes: !results.singleInstance.isValid,
    },
  };
};

// =============================================================================
// VALIDATION ERROR FORMATTING
// =============================================================================

/**
 * Formats validation errors for display in a modal
 * @param {Array} errors - Array of error objects
 * @returns {string} HTML formatted error list
 */
export const formatErrorsForDisplay = (errors) => {
  if (!errors || errors.length === 0) return '';

  const grouped = {
    llm: [],
    bifurcation: [],
    condition: [],
    connectivity: [],
    single_instance: [],
  };

  errors.forEach(error => {
    if (grouped[error.type]) {
      grouped[error.type].push(error);
    }
  });

  let html = '<div style="text-align: left;">';

  if (grouped.llm.length > 0) {
    html += '<p><strong>Configuração de LLM:</strong></p><ul>';
    grouped.llm.forEach(e => { html += `<li>${e.message}</li>`; });
    html += '</ul>';
  }

  if (grouped.bifurcation.length > 0) {
    html += '<p><strong>Conexões inválidas:</strong></p><ul>';
    grouped.bifurcation.forEach(e => { html += `<li>${e.message}</li>`; });
    html += '</ul>';
  }

  if (grouped.condition.length > 0) {
    html += '<p><strong>Nós de condição incompletos:</strong></p><ul>';
    grouped.condition.forEach(e => { html += `<li>${e.message}</li>`; });
    html += '</ul>';
  }

  if (grouped.connectivity.length > 0) {
    html += '<p><strong>Nós desconectados:</strong></p><ul>';
    grouped.connectivity.forEach(e => { html += `<li>${e.message}</li>`; });
    html += '</ul>';
  }

  if (grouped.single_instance.length > 0) {
    html += '<p><strong>Nós duplicados:</strong></p><ul>';
    grouped.single_instance.forEach(e => { html += `<li>${e.message}</li>`; });
    html += '</ul>';
  }

  html += '</div>';
  return html;
};
